import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { gsap } from "gsap";
import { useTranslation } from "react-i18next";
import { useLang } from "../LanguageContext";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const location = useLocation();
  const { lang, toggle } = useLang();
  const { t } = useTranslation();
  const navRef = useRef(null);
  const policyRef = useRef(null);

  const isHome = location.pathname === "/";
  const isTransparent = isHome && !scrolled;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setPolicyOpen(false);
  }, [location]);

  // Close policy dropdown when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (policyRef.current && !policyRef.current.contains(e.target)) {
        setPolicyOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Navbar fade-in on mount
  useEffect(() => {
    gsap.fromTo(
      navRef.current,
      { y: -20, opacity: 0 },
      { y: 0, opacity: 1, duration: 1, ease: "power2.out", delay: 0.3 }
    );
  }, []);

  // Magnetic effect on menu links
  const handleMouseMove = (e) => {
    const link = e.currentTarget;
    const rect = link.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    gsap.to(link, {
      x: x * 0.25,
      y: y * 0.25,
      duration: 0.3,
      ease: "power2.out",
    });
  };

  const handleMouseLeave = (e) => {
    gsap.to(e.currentTarget, {
      x: 0,
      y: 0,
      duration: 0.5,
      ease: "elastic.out(1, 0.5)",
    });
  };

  return (
    <nav
      ref={navRef}
      className={`navbar ${
        isTransparent ? "navbar-transparent" : "navbar-solid"
      } ${scrolled ? "navbar-scrolled" : ""}`}
    >
      <div className="navbar-container">

        {/* Left menu */}
        <div className="menu-left">
          <Link
            to="/about"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            {t("navbar.about")}
          </Link>
          <Link
            to="/contact"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            {t("navbar.contact")}
          </Link>
        </div>

        {/* Logo center */}
        <div
          className="logo"
          style={{
            visibility: isTransparent ? "hidden" : "visible",
            opacity: isTransparent ? 0 : 1,
            transition: "opacity 0.4s ease",
          }}
        >
          <Link to="/">
            <img
              src="/kmch_logo.jpg"
              alt="KMC Health"
              className="logo-img"
            />
          </Link>
        </div>

        {/* Right menu */}
        <div className="menu-right">

          {/* Policy dropdown (Privacy Policy + Cookie Policy) */}
          <div className="nav-dropdown" ref={policyRef}>
            <button
              type="button"
              className="nav-dropdown__trigger"
              onClick={() => setPolicyOpen((v) => !v)}
              onMouseEnter={() => setPolicyOpen(true)}
              aria-expanded={policyOpen}
              aria-haspopup="true"
            >
              {t("navbar.policyMenu")}
              <span
                className={`nav-dropdown__caret ${
                  policyOpen ? "nav-dropdown__caret--open" : ""
                }`}
              >
                ▾
              </span>
            </button>

            <div
              className={`nav-dropdown__menu ${
                policyOpen ? "nav-dropdown__menu--open" : ""
              }`}
              onMouseLeave={() => setPolicyOpen(false)}
            >
              <Link to="/privacy" onClick={() => setPolicyOpen(false)}>
                {t("navbar.privacy")}
              </Link>
              <Link to="/cookie-policy" onClick={() => setPolicyOpen(false)}>
                {t("navbar.cookiePolicy")}
              </Link>
            </div>
          </div>

          <button className="lang-toggle" onClick={toggle}>
            {lang}
          </button>
        </div>

        {/* Hamburger mobile */}
        <div
          className={`hamburger ${menuOpen ? "open" : ""}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span></span>
          <span></span>
          <span></span>
        </div>

        {/* Mobile dropdown */}
        <div className={`menu-mobile ${menuOpen ? "active" : ""}`}>
          <Link to="/about">{t("navbar.about")}</Link>
          <Link to="/contact">{t("navbar.contact")}</Link>
          <Link to="/privacy">{t("navbar.privacy")}</Link>
          <Link to="/cookie-policy">{t("navbar.cookiePolicy")}</Link>
          <button
            className="lang-toggle lang-toggle-mobile"
            onClick={toggle}
          >
            {lang}
          </button>
        </div>

      </div>
    </nav>
  );
}
